import type { ReactNode } from 'react';

import { useLingui } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useSelector } from 'react-redux';

import { getReactQueryAxiosInstance } from './api';
import {
  buildClaimSummaryGenerateRequestBody,
  type ClaimSummaryGenerateRequest,
} from './claimSummaryGenerate';
import { endpoints } from './endpoints';

export type PushToPMSRequest = ClaimSummaryGenerateRequest;

export interface PushToPMSResponse {
  success: boolean;
  message: ReactNode;
  batchId: string;
}

/**
 * React Query mutation hook for pushing claim attachments to PMS
 *
 * @param options - Optional mutation options
 * @returns React Query mutation object
 */
export const usePushToPMS = (
  options?: Parameters<
    typeof useMutation<PushToPMSResponse, AxiosError, PushToPMSRequest>
  >[0],
) => {
  const { t } = useLingui();
  const user = useSelector(
    (state: {
      login?: {
        user?: { primaryPracticeId?: string; primaryOrganizationId?: string };
      };
    }) => state?.login?.user,
  );
  const currentPracticeId = user?.primaryPracticeId;
  const currentOrganizationId = user?.primaryOrganizationId;

  return useMutation({
    mutationFn: async (
      request: PushToPMSRequest,
    ): Promise<PushToPMSResponse> => {
      if (!currentOrganizationId || !currentPracticeId) {
        throw new Error('Missing organization or practice information');
      }

      const instance = getReactQueryAxiosInstance();
      const requestBody = buildClaimSummaryGenerateRequestBody(request);

      // Call generate-pieces endpoint to get PNG pieces and practice document IDs
      const generateResponse = await instance.request<Blob>({
        method: 'POST',
        url: `/${endpoints.generatePieces}`,
        headers: {
          Practice: currentPracticeId,
          Org: currentOrganizationId,
        },
        data: requestBody,
        responseType: 'blob',
      });

      // Extract practice document IDs from response header (JSON string)
      const practiceDocumentIdsHeader =
        generateResponse.headers['x-practice-document-ids'];

      let practiceDocumentIds: string[] = [];
      try {
        const parsed = JSON.parse(practiceDocumentIdsHeader || '[]');
        practiceDocumentIds = Array.isArray(parsed) ? parsed : [];
      } catch {
        throw new Error(
          'Failed to parse practice document IDs from response header',
        );
      }

      if (!practiceDocumentIds.length) {
        throw new Error('No practice document IDs returned from generation');
      }

      // Call writeback endpoint with array of document IDs
      const writebackResponse = await instance.request<{ batchId: string }>({
        method: 'POST',
        url: `/${endpoints.claimWriteback}`,
        headers: {
          Practice: currentPracticeId,
          Org: currentOrganizationId,
        },
        data: {
          practiceDocumentIds,
        },
      });

      return {
        success: true,
        message: t`Push to PMS initiated`,
        batchId: writebackResponse.data.batchId,
      };
    },
    ...options,
  });
};
